import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { graphql, Link, Script, PageRendererProps } from 'gatsby';
import querystring from 'query-string';

import { GetSanityProductPageQuery } from '@repo/models/gatsby-graphql-types';
import { DealerSearchContext } from '@repo/contexts/DealerSearchContext';
import { LocaleContext } from '@repo/contexts/LocaleContext';
import getProductSchema from '@repo/utils/getProductSchema';
import hasCompanyOwnedDealerVariants from '@repo/utils/hasCompanyOwnedDealerVariants';
import BazaarVoiceContainer from '@repo/ui-components/bazaarvoice/BazaarVoiceContainer';
import Reviews from '@repo/ui-components/bazaarvoice/Reviews';
import QandA from '@repo/ui-components/bazaarvoice/QandA';
import BlockBuilder from '@repo/ui-components/sanity/BlockBuilder';
import BreadcrumbWithWrapper, {
  BreadcrumbElement,
} from '@repo/ui-components/BreadcrumbWithWrapper';
import Button from '@repo/ui-components/button/Button';
import ContentArea from '@repo/ui-components/product-container/ContentArea';
import DealerGateModal from '@repo/ui-components/dealer-search/DealerGateModal';
import PageWrapper from '@repo/ui-components/page-wrapper/PageWrapper';
import RelatedProducts from '@components/RelatedProducts';
import ProductContainer from '@repo/ui-components/product-container/ProductContainer';
import { formatReviewSku } from '@repo/utils';

import Layout from '@components/layout';
import getInternalLink from '@components/utils/getInternalLink';
import styles from '@components/utils/styles';
import generateHeadTagData from '../utils/generateHeadTagData';

declare global {
  interface Window {
    $BV?: {
      configure: (scope: string, options: any) => void;
      ui: (
        section: 'rr' | 'qa',
        action: string,
        options: { doShowContent: () => void }
      ) => void;
    };
  }
}

declare module 'gatsby' {
  interface ScriptProps {
    fetchPriority?: 'high' | 'low' | 'auto';
  }
}

function recalculatePriceRange(variants) {
  let maxPrice = 0;
  let minPrice = Number.MAX_SAFE_INTEGER;

  variants.forEach(variant => {
    const { price } = variant;

    if (price > maxPrice) {
      maxPrice = price;
    }

    if (price < minPrice) {
      minPrice = price;
    }
  });

  return {
    maxVariantPrice: {
      amount: maxPrice,
      currencyCode: 'USD',
    },
    minVariantPrice: {
      amount: minPrice,
      currencyCode: 'USD',
    },
  };
}

function matchesSelectedOption(selectedOptions, optionName, value) {
  return selectedOptions.some(
    selectedOption =>
      selectedOption.name === optionName && selectedOption.value === value
  );
}

function hasMatchingVariant(optionName, value, variants) {
  return variants.some(variant =>
    matchesSelectedOption(variant.selectedOptions, optionName, value)
  );
}

function getMatchingValues(option, variants) {
  return option.values.filter(value =>
    hasMatchingVariant(option.name, value, variants)
  );
}

// filter options based on variants
function filterOptionsByVariants(options, variants) {
  return options.map(option => ({
    name: option.name,
    values: getMatchingValues(option, variants),
  }));
}

// split product variants into company owned and non company owned
const filterCompanyOwnedDealerProduct = (
  product,
  isCompanyOwnedDealer,
  sanityProduct
) => {
  const filteredSanityProduct = sanityProduct;
  const { variants, options } = product;

  const companyOwnedVariants = [];
  const nonCompanyOwnedVariants = [];

  variants.forEach(item => {
    const metafield = item.metafields.find(
      variantMetafield =>
        variantMetafield.key === 'company_owned_dealer_variant'
    );
    if (metafield && metafield.value === 'true') {
      companyOwnedVariants.push(item);
    } else {
      nonCompanyOwnedVariants.push(item);
    }
  });

  filteredSanityProduct.store.variants =
    filteredSanityProduct.store.variants.filter(v => {
      const codVariant = v.store.metafields.find(
        variantMetafield =>
          variantMetafield.key === 'company_owned_dealer_variant' ||
          variantMetafield.name === 'company_owned_dealer_variant'
      );

      return isCompanyOwnedDealer
        ? codVariant?.value === 'true'
        : codVariant?.value === 'false';
    });

  const dealerSpecificProduct = {
    ...product,
    variants: isCompanyOwnedDealer
      ? companyOwnedVariants
      : nonCompanyOwnedVariants,
    options: isCompanyOwnedDealer
      ? filterOptionsByVariants(options, companyOwnedVariants)
      : filterOptionsByVariants(options, nonCompanyOwnedVariants),
  };

  // recalculate price range based on filtered variants
  dealerSpecificProduct.priceRangeV2 = recalculatePriceRange(
    dealerSpecificProduct.variants
  );

  return { filteredProduct: dealerSpecificProduct, filteredSanityProduct };
};

interface ProductDetailTemplateProps {
  data: GetSanityProductPageQuery;
}

function ProductDetailTemplate({
  data,
  location,
}: Readonly<ProductDetailTemplateProps & PageRendererProps>) {
  const [productSchemaData, setProductSchemaData] = useState(null);
  const { currency } = useContext(LocaleContext);
  const query = querystring.parse(location.search);
  const { sanityProduct } = data;
  const {
    SEOMetadata,
    store,
    description,
    relatedProducts,
    collection,
    manuals,
    supportPage,
    marketingContent,
  } = sanityProduct;

  const bvRef = useRef(null);

  const { storedDealer } = useContext(DealerSearchContext);

  const isDealerSpecificProduct = hasCompanyOwnedDealerVariants(
    sanityProduct.product
  );

  const [activeSku] = useState(query?.sku ?? null);
  const [productState, setProductState] = useState({
    product: sanityProduct.product,
    filteredSanityProduct: null,
    noFilteredDealerVariants: false,
    selectedVariant:
      sanityProduct.product?.variants?.find(
        variant => variant.sku === activeSku
      ) || sanityProduct.product?.variants[0],
    productSchema: null,
  });

  const updateProductState = useCallback(() => {
    if (isDealerSpecificProduct && storedDealer) {
      const { filteredProduct, filteredSanityProduct } =
        filterCompanyOwnedDealerProduct(
          sanityProduct.product,
          storedDealer.isCOS,
          sanityProduct
        );

      if (filteredProduct.variants.length === 0) {
        setProductState({
          product: filteredProduct,
          filteredSanityProduct,
          noFilteredDealerVariants: true,
          selectedVariant: null,
          productSchema: null,
        });
      } else {
        const selectedVariant =
          filteredProduct.variants.find(variant => variant.sku === activeSku) ||
          filteredProduct.variants[0];

        setProductState({
          product: filteredProduct,
          filteredSanityProduct,
          noFilteredDealerVariants: false,
          selectedVariant,
          productSchema: getProductSchema(filteredProduct, null, activeSku),
        });
      }
    } else {
      const selectedVariant =
        sanityProduct.product?.variants?.find(
          variant => variant.sku === activeSku
        ) || sanityProduct.product?.variants[0];

      setProductState({
        product: sanityProduct.product,
        filteredSanityProduct: sanityProduct,
        noFilteredDealerVariants: false,
        selectedVariant,
        productSchema: getProductSchema(sanityProduct.product, null, activeSku),
      });
    }
  }, [activeSku, isDealerSpecificProduct, sanityProduct, storedDealer]);

  useEffect(() => {
    updateProductState();
  }, [storedDealer, updateProductState]);

  const breadcrumbs = [
    { link: getInternalLink({ slug: '/', currency }), text: 'Home' },
  ] as BreadcrumbElement[];

  const shippingPolicyLink =
    currency === 'CAD' ? '/en-ca/shipping-policy' : '/shipping-policy';
  const returnPolicyNode = (
    <p>
      <b>Satisfaction guaranteed:</b>{' '}
      <Link to={currency === 'CAD' ? '/en-ca/return-policy' : '/return-policy'}>
        return your product
      </Link>{' '}
      within 45 days
    </p>
  );

  if (collection?.parentCollection?.store?.title) {
    breadcrumbs.push({
      text: collection.parentCollection.store.title,
      link: getInternalLink({
        slug: `/${collection.parentCollection.store.slug.current}`,
        currency,
      }),
    });
    breadcrumbs.push({
      text: collection.store.title,
      link: getInternalLink({
        slug: `/${collection.parentCollection.store.slug.current}/${collection.store.slug.current}`,
        currency,
      }),
    });
  } else if (collection) {
    breadcrumbs.push({
      text: collection.store.title,
      link: getInternalLink({
        slug: `/${collection.store.slug.current}`,
        currency,
      }),
    });
  }

  breadcrumbs.push({ text: productState.product?.title });

  const dealerModalActive =
    isDealerSpecificProduct &&
    (!storedDealer || productState.noFilteredDealerVariants);

  const planUnavailable =
    (productState.product?.tags?.includes('service-plan') ||
      productState.product?.tags?.includes('complete-care-plan')) &&
    productState.noFilteredDealerVariants;

  const planType = productState.product?.tags?.includes('service-plan')
    ? 'Service Plan'
    : 'Complete Care Plan';

  const {
    product,
    filteredSanityProduct,
    noFilteredDealerVariants,
    selectedVariant,
    productSchema,
  } = productState;

  const hasSizeChart = marketingContent?.blocks?.some(
    block => block._type === 'blockBuilderSizeChart'
  );

  // Watch active sku parameter and update product schema data if sku changes
  useEffect(() => {
    setProductSchemaData(JSON.stringify(getProductSchema(product, activeSku)));
  }, [activeSku, product]);

  useEffect(() => {
    let intervalId;

    const handleScroll = () => {
      document
        .getElementById('bv-qanda-container')
        .scrollIntoView({ behavior: 'smooth' });
    };
    const checkElementsAndAddListeners = () => {
      const elements = document.querySelectorAll(
        '.bv-qa-stats-questions, .bv-qa-stats-answers'
      );

      if (elements.length > 0) {
        elements.forEach(element => {
          element.addEventListener('click', handleScroll);
        });

        // Clear the interval once the elements are found and listeners are added
        clearInterval(intervalId);
      }
    };

    // Check for the elements every 500ms
    intervalId = setInterval(checkElementsAndAddListeners, 500);

    // Cleanup function to remove event listeners and clear the interval when the component unmounts
    return () => {
      const elements = document.querySelectorAll(
        '.bv-qa-stats-questions, .bv-qa-stats-answers'
      );
      elements.forEach(element => {
        element.removeEventListener('click', handleScroll);
      });
      clearInterval(intervalId);
    };
  }, []);

  return (
    <Layout
      includeBVScript
      location={location}
      headTagData={generateHeadTagData(
        SEOMetadata,
        product,
        description,
        productSchema,
        selectedVariant,
        location,
        store
      )}
      scriptsData={[
        <script type="application/ld+json" key="pdp-schema">
          {productSchemaData}
        </script>,
      ]}
    >
      <DealerGateModal
        active={dealerModalActive}
        planUnavailable={planUnavailable}
        planType={planType}
      />
      <BreadcrumbWithWrapper elements={breadcrumbs} />

      <ContentArea>
        <ProductContainer
          product={product}
          sanityProduct={filteredSanityProduct}
          activeSku={activeSku}
          downloadsSection={{
            background: styles.colors.gray[50],
            textColor: styles.colors.blue[500],
            supportLink: supportPage,
            heading: 'Support',
          }}
          productData={{ supportManuals: manuals }}
          disableAddToCart={
            (isDealerSpecificProduct && !storedDealer) ||
            noFilteredDealerVariants
          }
          useEnhancedPDPMessaging
          enhancedPDPMessages={{
            shippingPolicyLink,
            returnPolicyNode,
          }}
          atGlance={sanityProduct?.atGlance}
          hasSizeChart={hasSizeChart}
          loadOldBV
        />
      </ContentArea>
      <PageWrapper padding="1rem">
        <BlockBuilder
          blocks={marketingContent?.blocks}
          getInternalLink={getInternalLink}
          Button={Button}
        />

        {relatedProducts?.length > 0 && (
          <RelatedProducts products={relatedProducts} />
        )}
      </PageWrapper>

      <BazaarVoiceContainer ref={bvRef} id="bazaarVoice">
        <Reviews id="bv-reviews-container" sku={selectedVariant?.sku} />

        <div id="bv-qanda-container">
          <QandA sku={selectedVariant?.sku} />
        </div>
      </BazaarVoiceContainer>
      <Script
        defer
        fetchPriority="low"
        src={`https://apps.bazaarvoice.com/deployments/invisiblefence/main_site/${process.env.GATSBY_BAZAARVOICE_ENVIRONMENT}/en_US/bv.js`}
        onLoad={() => {
          window?.$BV?.configure('global', {
            productId: `${formatReviewSku(product.variants[0].sku)}`,
          });

          window?.$BV?.ui('rr', 'show_reviews', {
            doShowContent() {},
          });

          window?.$BV?.ui('qa', 'show_questions', {
            doShowContent() {},
          });
        }}
      />
    </Layout>
  );
}

export const query = graphql`
  query GetSanityProductPage($handle: String = "") {
    sanityProduct(
      store: { slug: { current: { eq: $handle } } }
      siteIdentifier: { eq: "ifb" }
    ) {
      description
      atGlance {
        keyItem
        valueItem
      }
      marketingContent: _rawMarketingContent(
        resolveReferences: { maxDepth: 15 }
      )
      SEOMetadata {
        title
        description
        canonicalUrl
        noIndex
        noFollow
        ogTitle
        ogDescription
        ogUrl
        ogImage {
          asset {
            url
          }
        }
      }
      store: _rawStore(resolveReferences: { maxDepth: 3 })
      discontinued
      supportPage
      relatedProducts {
        features
        store {
          previewImageUrl
          variants {
            canadaStore {
              compareAtPrice
              price
            }
          }
        }
        shopifyProduct {
          shopifyId
          title
          handle
          productType
          tags
          priceRangeV2 {
            minVariantPrice {
              amount
            }
            maxVariantPrice {
              amount
            }
          }
          variants {
            requiresShipping
            shopifyId
            sku
            price
          }
          vendor
        }
      }
      manuals {
        asset: _rawAsset(resolveReferences: { maxDepth: 3 })
      }
      collection {
        store {
          title
          slug {
            current
          }
        }
        parentCollection {
          store {
            title
            slug {
              current
            }
          }
        }
      }
      product: shopifyProduct {
        shopifyId
        title
        handle
        productType
        description
        media {
          id
          ... on ShopifyMediaImage {
            id
            image {
              altText
              src
              width
              height
            }
          }
          ... on ShopifyVideo {
            __typename
            id
            sources {
              url
              format
              width
              height
            }
            preview {
              image {
                src
                altText
                width
                height
              }
            }
          }
          ... on ShopifyExternalVideo {
            __typename
            id
            embeddedUrl
            preview {
              image {
                src
                altText
                width
                height
              }
            }
          }
        }
        vendor
        tags
        priceRangeV2 {
          maxVariantPrice {
            amount
            currencyCode
          }
          minVariantPrice {
            amount
            currencyCode
          }
        }
        options {
          name
          values
        }
        variants {
          title
          requiresShipping
          shopifyId
          availableForSale
          sku
          metafields {
            key
            value
          }
          selectedOptions {
            name
            value
          }
          price
          compareAtPrice
          media {
            id
            ... on ShopifyMediaImage {
              id
              image {
                src
                altText
              }
            }
          }
        }
      }
    }
  }
`;

export default ProductDetailTemplate;
